<template>
  <img
    v-if="!envConfig.renderPag"
    class="magic-ui-image"
    :class="{ 'in-editor': inEditor }"
    :src="envConfig.src"
    :style="config.style"
    @click="clickHandler"
    @keydown.enter="clickHandler"
  >
  <div
    v-else
    ref="pagArea"
    class="magic-ui-image"
    :class="{ 'in-editor': inEditor }"
    :style="config.style"
    @click="clickHandler"
    @keydown.enter="clickHandler"
  >
    <canvas
      :id="`id_${config.id}`"
      :style="pagStyle"
    />
  </div>
</template>

<script>
import 'whatwg-fetch';
import { getSearchObj, setUrlParam } from '@tencent/magic-ui-util';

export default {
  name: 'MagicUiImage',

  inject: {
    hoc: {
      default: {},
    },
  },

  props: {
    config: {
      type: Object,
      default() {
        return {};
      },
    },

    model: {
      type: [Object, Array],
      default() {
        return [];
      },
    },
  },

  data() {
    return {
      magicEnvConfig: null,
      inEditor: this.inMagic,
      pagJsLoadPromise: null,
      timeNum: 0,
      pagView: null,
      pagStyle: {
        width: '0px',
        height: '0px',
      },
      curPage: null,
    };
  },

  computed: {
    /**
     *
      1、扫码的页面链接带有以下参数时，强制前端展示【其他】价格
      p_iosH5Pay=1
      p_kt_boss_channel=xxx
      p_fh5=1

      2、彩云页面可将以上参数透传至开通页，开通页根据透传参数强制走非iap价格
     *
    */
    deliveryParams() {
      if (this.inHippy) {
        return {};
      }
      const params = getSearchObj();
      return Object.keys(params).reduce((obj, name) => {
        if (name.startsWith('p_') && name.substring(2)) {
          const key = name.substring(2);
          return { ...obj, [key]: params[name] };
        }
        return obj;
      }, {});
    },

    envName() {
      const { isIphone, isIpad, isApp, isMqq, isAndroid, isWechat } = this.env;
      if (isIphone && isApp) {
        return 'iphoneApp';
      }
      if (isIphone && isMqq) {
        return 'iphoneMqq';
      }
      if (isIpad && isApp) {
        return 'ipadApp';
      }
      if (isIpad && isMqq) {
        return 'ipadMqq';
      }
      if (isAndroid && isApp) {
        return 'androidApp';
      }
      if (isWechat) {
        return 'wechatH5';
      }
      return 'h5';
    },

    envConfig() {
      if (this.inMagic && this.magicEnvConfig) {
        return this.magicEnvConfig;
      }
      return this.getEnvConfig();
    },
  },

  watch: {
    'envConfig.renderPag'(newValue) {
      if (newValue && !this.pagJsLoadPromise) {
        this.initPagJsLoadPromise();
      }
      if (newValue && this.envConfig.pagSrc) {
        this.initPag();
      }
    },
    'envConfig.pagSrc'(newValue) {
      if (this.envConfig.renderPag && newValue) {
        this.initPag();
      }
    },
  },

  async created() {
    if (this.config.hiddenAtStart) {
      const shouldVisible = () => {
        const doc = document.scrollingElement;
        this.visible = doc.scrollTop > 30 ? this.hoc.show() : this.hoc.hide();
      };

      window.onscroll = () => {
        shouldVisible();
      };
      shouldVisible();
    }
    if (this.envConfig.renderPag) {
      this.initPagJsLoadPromise();
    }
  },

  async mounted() {
    if (this.envConfig.renderPag && this.envConfig.pagSrc) {
      window.addEventListener('resize', this.resizeCanvas);
      this.initPag();
    }
  },
  unmounted() {
    window.removeEventListener('resize', this.resizeCanvas);
  },

  methods: {
    getEnvConfig() {
      const { iosH5Pay, fh5, kt_boss_channel: ktBossChannel } = this.deliveryParams;
      if (+iosH5Pay === 1 || +fh5 === 1 || ktBossChannel) {
        return (this.config.envConfigs || []).find(item => item.channels.includes('h5')) || {};
      }
      const envConfig = (this.config.envConfigs || [])
        .find(item => item.channels.includes(this.envName));
      return envConfig || {};
    },
    async clickHandler() {
      if (this.config.handler) {
        this.config.handler(this);
        return;
      }

      let { href } = this.envConfig;

      if (typeof href === 'function') {
        href = await href.bind(this)(this, {
          model: this.model,
        });
      }

      if (href) {
        Object.keys(this.deliveryParams).forEach((name) => {
          if (name) {
            href = setUrlParam(name, this.deliveryParams[name], href);
          }
        });
        this.app.openUrl(href);
      }
    },
    resizeCanvas() {
      if (this.envConfig.renderPag && this.envConfig.pagSrc) {
        if (this.timeNum) {
          clearTimeout(this.timeNum);
          this.timeNum = 0;
        }
        this.timeNum = setTimeout(() => {
          const styleDeclaration = window.getComputedStyle(this.$refs.pagArea, null);
          this.pagStyle.width = styleDeclaration.width;
          this.pagStyle.height = styleDeclaration.height;
          const canvas = document.getElementById(`id_${this.config.id}`);
          canvas.width = Number(styleDeclaration.width.replace('px', ''));
          canvas.height = Number(styleDeclaration.height.replace('px', ''));
          if (this.pagView) {
            this.pagView.updateSize();
            this.pagView.flush();
          }
        }, 300);
      }
    },
    async initPag() {
      await this.pagJsLoadPromise;
      let PAG = this.curPage;
      if (!PAG) {
        PAG = await window.libpag.PAGInit();
      }
      const buffer = await fetch(this.envConfig.pagSrc).then(res => res.arrayBuffer());
      const pagFile = await PAG.PAGFile.load(buffer);
      const styleDeclaration = window.getComputedStyle(this.$refs.pagArea, null);
      this.pagStyle.width = styleDeclaration.width;
      this.pagStyle.height = styleDeclaration.height;
      const canvas = document.getElementById(`id_${this.config.id}`);
      canvas.width = Number(styleDeclaration.width.replace('px', ''));
      canvas.height = Number(styleDeclaration.height.replace('px', ''));
      this.pagView = await PAG.PAGView.init(pagFile, `#id_${this.config.id}`);
      this.pagView.setRepeatCount(0);
      await this.pagView.play();
    },
    initPagJsLoadPromise() {
      this.pagJsLoadPromise = new Promise((resolve) => {
        if (window.libpag) {
          resolve();
          return;
        }
        const pagScript = document.createElement('script');
        pagScript.setAttribute('src', 'https://cdn.jsdelivr.net/npm/libpag@latest/lib/libpag.min.js');
        document.body.appendChild(pagScript);
        pagScript.addEventListener('load', () => {
          resolve();
        });
      });
    },
  },
};
</script>

<style lang="scss">
img.magic-ui-image {
  display: none;
  &[src] {
    display: block;
  }
  &.in-editor {
    display: block;
  }
}
</style>

